// TODO: (vliu) This is used in architect only and can be removed after we update architect
// to use same list style as recordings page

/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components/macro';

import { DropdownInput, DropdownInputReadOnly } from './DropdownInput';
import { DropdownList, DropdownListContainer } from './DropdownList';
import { InputChipList } from './InputChip';
import { DropdownData, DropdownSectionInfo } from '../types';
import { mod } from '../utils/math';

const HorizontalSelectorDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;

  /* Allow for the dropdown to go below the input */
  overflow-y: visible;
`;

const HorizontalInputContainer = styled.div`
  /* Allow for the dropdown list to be positioned relative to this form
  container*/
  position: relative;

  /* Reveal dropdown list when the text input is focused */
  &:focus-within {
    ${DropdownListContainer} {
      display: flex;
    }
  }
`;

const VerticalSelectorDiv = styled.div<{ isInputFocused: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  /* Add delimiter between Input+Chips and dropdown list when they are
  stacked vertically */
  ${DropdownListContainer} {
    border-top: 1px solid ${(props) => props.theme.delimiterColorLight};
  }

  /* Here we want to ensure dropdown list is revealed when the text
  input is focused. focus-within is not an option unfortunately because
  the InputChipList is also a child of this div and we do not want to
  reveal the dropdown list when removing a selected element. Instead we
  deal with this with javascript which sets the 'isInputFocused' prop */
  ${(props) =>
    props.isInputFocused
      ? css`
          ${DropdownListContainer} {
            display: flex;
          }
        `
      : ''}
`;

const VerticalInputAndChipsContainer = styled.div`
  width: 100%;

  /* Give breathing room between input+chips and dropdown list */
  padding-left: 16px;
  padding-right: 16px;
  padding-bottom: 8px;

  background-color: ${(props) => props.theme.bgColor};

  /* Keep input chips below the text input*/
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`;

// Keep a list of all the DropdownData values that we can easily cycle
// between with a keyboard
const flattenMapValues = (
  myMap: Map<DropdownSectionInfo, DropdownData[]>,
  filteredOutValues: string[]
) => {
  const result: DropdownData[] = [];
  myMap.forEach((values) =>
    values
      .filter((v) => !filteredOutValues.includes(v.name))
      .forEach((v) => result.push(v))
  );
  return result;
};

// Internally keep track of the various states of the input we can switch between
enum InputState {
  disabled = 'DISABLED',
  readonly = 'READONLY',
  default = 'DEFAULT',
}

// Should fit within a container of a given size
const DropdownSelector: React.FC<{
  options: Map<DropdownSectionInfo, DropdownData[]>;
  selectedItems: string[];
  onSelectionChange: (newSelection: string[]) => void;
  placeholder: string;
  vertical: boolean;
  height?: string;
  setInputHandle?: (input: HTMLInputElement) => void;
  onInputFocusChange?: (isFocused: boolean) => void;
  showListInPopup?: boolean;
  disabled?: boolean;
  allowMultiSelections?: boolean;
}> = ({
  options,
  selectedItems,
  onSelectionChange,
  placeholder,
  setInputHandle,
  onInputFocusChange,
  vertical,
  height,
  showListInPopup = !vertical,
  disabled = false,
  allowMultiSelections = true,
}) => {
  // Keep track of user input and of the options filtered by it
  const [userInput, setUserInput] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);

  // List of options avaible for the user to cycle between using the keyboard
  const [flattenedOptions, setFlattenedOptions] = useState(
    flattenMapValues(options, selectedItems)
  );
  // Current focused selection with the keyboard
  const [keyboardSelectionIndex, setKeyboardSelectionIndex] = useState(0);

  // When user inputs some text, filter the list of options using based
  // on what they typed
  useEffect(() => {
    const filteredMap = new Map<DropdownSectionInfo, DropdownData[]>();
    for (const [section, values] of options.entries()) {
      const filteredValues = values.filter((v) =>
        v.name.toLowerCase().includes(userInput.toLowerCase())
      );
      if (filteredValues.length > 0) {
        filteredMap.set(section, filteredValues);
      }
    }
    // TODO(pierre) on 2021-04-25: Need lodash isEqual here to only set
    // the state when changed?
    setFilteredOptions(filteredMap);
  }, [userInput, options]);

  // When the filtering changes, recompute the values the keyboard can cycle between
  useEffect(() => {
    setFlattenedOptions(flattenMapValues(filteredOptions, selectedItems));
  }, [filteredOptions, selectedItems]);

  // Reset the keyboard selector to the top when a new set of values are filtered
  useEffect(() => {
    setKeyboardSelectionIndex(0);
  }, [flattenedOptions]);

  // To call when new item is selected
  const onSelect = useCallback(
    (selectedItem: string) => {
      if (selectedItems.indexOf(selectedItem) === -1) {
        onSelectionChange(selectedItems.concat([selectedItem]));
        // After each selection, clear the user input since it's most
        // likely users now want to select a different item, and it's
        // annoying to have to press 'Backspace' a bunch of times
        setUserInput('');
      }
    },
    [selectedItems, onSelectionChange]
  );

  // Keep track of input state to know what type of input component to
  // render
  const [inputState, setInputState] = useState(InputState.default);
  useEffect(() => {
    if (disabled) {
      setInputState(InputState.disabled);
    } else if (!allowMultiSelections && selectedItems.length > 0) {
      setInputState(InputState.readonly);
    } else {
      setInputState(InputState.default);
    }
  }, [disabled, allowMultiSelections, selectedItems]);

  // Keep track of input focus state so we can update styling based on
  // it
  const [isInputFocused, setIsInputFocused] = useState(false);
  useEffect(() => {
    onInputFocusChange?.(isInputFocused);
  }, [isInputFocused, onInputFocusChange]);

  // When the input becomes disabled or readonly, set focus to false. We
  // need to call this explicitly because when we delete the old default
  // input component, onBlur will not be called (because the element has
  // disappeared)
  useEffect(() => {
    if (inputState !== InputState.default) {
      setIsInputFocused(false);
      setUserInput(''); // Reset the input state
    }
  }, [inputState]);

  const inputMinWidth = useCallback(
    () => (vertical ? undefined : '250px'),
    [vertical]
  );

  const dropdownListEl = inputState === InputState.default && (
    <DropdownList
      options={filteredOptions}
      onClicked={(selectedItem) => {
        onSelect(selectedItem);
      }}
      keyboardFocusedOption={
        flattenedOptions.length > keyboardSelectionIndex
          ? flattenedOptions[keyboardSelectionIndex].name
          : ''
      }
      userInput={userInput}
      alreadySelectedOptions={selectedItems}
      alwaysVisible={!showListInPopup}
    />
  );

  const inputElement = () => {
    switch (inputState) {
      case InputState.disabled:
        return (
          <DropdownInput
            placeholder={placeholder}
            userInput=""
            onInputChange={() => {}}
            minWidth={inputMinWidth()}
            disabled
          />
        );
      case InputState.readonly:
        return (
          <DropdownInputReadOnly
            input={selectedItems?.[0]}
            minWidth={inputMinWidth()}
            onReset={() => onSelectionChange([])}
          />
        );
      default:
        break;
    }
    return (
      <DropdownInput
        placeholder={placeholder}
        userInput={userInput}
        onInputChange={(newInput) => setUserInput(newInput)}
        onKeyboardDown={() => {
          const n = flattenedOptions.length;
          setKeyboardSelectionIndex((previousIndex) =>
            mod(previousIndex + 1, n)
          );
        }}
        onKeyboardUp={() => {
          const n = flattenedOptions.length;
          setKeyboardSelectionIndex((previousIndex) =>
            mod(previousIndex - 1, n)
          );
        }}
        onKeyboardEnter={() => {
          if (flattenedOptions.length > 0) {
            onSelect(flattenedOptions[keyboardSelectionIndex].name);
          }
        }}
        setInputHandle={setInputHandle}
        minWidth={inputMinWidth()}
        onFocusChange={setIsInputFocused}
      />
    );
  };

  const inputChipListEl = inputState === InputState.default &&
    allowMultiSelections &&
    selectedItems.length > 0 && (
      <InputChipList
        names={selectedItems}
        onRemove={(removedItem) => {
          onSelectionChange(selectedItems.filter((val) => val !== removedItem));
        }}
        height={height}
      />
    );

  return vertical ? (
    <VerticalSelectorDiv isInputFocused={isInputFocused}>
      <VerticalInputAndChipsContainer>
        {inputElement()}
        {inputChipListEl}
      </VerticalInputAndChipsContainer>
      {dropdownListEl}
    </VerticalSelectorDiv>
  ) : (
    <HorizontalSelectorDiv>
      <HorizontalInputContainer>
        {inputElement()}
        {dropdownListEl}
      </HorizontalInputContainer>
      {inputChipListEl}
    </HorizontalSelectorDiv>
  );
};

export { DropdownSelector, VerticalSelectorDiv };
